import React from 'react';
import { useWeightUnit } from '../../hooks/useWeightUnit';
import { formatConvertedAmount, fromGrams } from '../../utils/weightUnits';
import './MealBucket.css';

const MEAL_ICONS = {
  breakfast: 'fa-sun',
  lunch: 'fa-cloud-sun',
  dinner: 'fa-moon',
  snacks: 'fa-cookie-bite'
};

const MEAL_COLORS = {
  breakfast: '#f59e0b',
  lunch: '#10b981',
  dinner: '#667eea',
  snacks: '#ec4899'
};

const MealBucket = ({ 
  mealType, 
  foods = [], 
  menuItems = [], 
  totals,
  onRemoveFood,
  onRemoveMenuItem,
  onUpdateFoodQuantity,
  onUpdateMenuItemServings,
  onClearMeal
}) => {
  const weightUnit = useWeightUnit();
  const hasItems = foods.length > 0 || menuItems.length > 0;
  const icon = MEAL_ICONS[mealType] || 'fa-utensils';
  const color = MEAL_COLORS[mealType] || '#718096';

  const formatNutrient = (value, unit = 'g') => {
    if (value === undefined || value === null) return '-';
    return `${Math.round(value * 10) / 10}${unit}`;
  };

  const parseNutrition = (value) => {
    if (!value) return 0;
    if (typeof value === 'number') return value;
    return parseFloat(value.replace(/[^0-9.]/g, '')) || 0;
  };

  const formatServingSize = (servingSize) => {
    if (!servingSize) return '';
    const mass = fromGrams(servingSize, weightUnit);
    return `${formatConvertedAmount(mass.quantity, mass.unit)} ${mass.unit}`;
  };

  return (
    <div className="meal-bucket" style={{ '--meal-color': color }}>
      <div className="bucket-header">
        <div className="bucket-title">
          <i className={`fas ${icon}`}></i>
          <span>{mealType.charAt(0).toUpperCase() + mealType.slice(1)}</span>
          <span className="item-count">({foods.length + menuItems.length})</span>
        </div>
        {totals && (
          <div className="bucket-totals">
            <span className="total-calories">{totals.calories} cal</span>
            <span className="total-protein">{totals.protein}g P</span>
          </div>
        )}
      </div>

      <div className="bucket-content">
        {hasItems ? (
          <div className="bucket-items">
            {/* Menu Items */}
            {menuItems.map((item, index) => {
              const menuItem = item.menuItem || {};
              const itemId = menuItem._id || menuItem.menuItemId || `menu-${index}`;
              const title = menuItem.title || 'Recipe';
              const nutrition = menuItem.nutrition || {};
              const calories = parseNutrition(nutrition.Calories) * (item.servings || 1);
              const protein = parseNutrition(nutrition.Protein) * (item.servings || 1);
              
              return (
                <div key={itemId} className="bucket-item menu-item-entry">
                  <div className="item-icon recipe">
                    <i className="fas fa-book-open"></i>
                  </div>
                  <div className="item-info">
                    <span className="item-name">{title}</span>
                    <span className="item-nutrition">
                      {calories} cal | {protein}g P
                    </span>
                  </div>
                  <div className="item-controls">
                    <span className="servings-label">servings</span>
                    <button 
                      className="qty-btn"
                      onClick={() => onUpdateMenuItemServings(itemId, mealType, (item.servings || 1) - 1)}
                    >
                      <i className="fas fa-minus"></i>
                    </button>
                    <span className="qty-value">{item.servings || 1}</span>
                    <button 
                      className="qty-btn"
                      onClick={() => onUpdateMenuItemServings(itemId, mealType, (item.servings || 1) + 1)}
                    >
                      <i className="fas fa-plus"></i>
                    </button>
                    <button 
                      className="remove-btn"
                      onClick={() => onRemoveMenuItem(itemId, mealType)}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              );
            })}

            {/* Individual Foods */}
            {foods.map((item, index) => {
              const food = item.food || {};
              const foodId = food.fdcId || `food-${index}`;
              const description = food.description || 'Food item';
              const nutrients = food.nutrients || {};
              const quantity = item.quantity || 1;
              const servingSize = item.servingSize || 100;
              const scaleFactor = (servingSize / 100) * quantity;
              const calories = (nutrients.calories || 0) * scaleFactor;
              const protein = (nutrients.protein || 0) * scaleFactor;
              const brandOwner = food.brandOwner;
              
              return (
                <div key={foodId} className="bucket-item food-entry">
                  <div className="item-icon food">
                    <i className="fas fa-leaf"></i>
                  </div>
                  <div className="item-info">
                    <span className="item-name">{description}</span>
                    {brandOwner && (
                      <span className="item-brand">{brandOwner}</span>
                    )}
                    <span className="item-nutrition">
                      {formatNutrient(calories, '')} cal | {formatNutrient(protein)} P
                    </span>
                    <span className="item-serving-info">
                      {formatServingSize(servingSize)} per serving
                    </span>
                  </div>
                  <div className="item-controls">
                    <span className="servings-label">servings</span>
                    <button 
                      className="qty-btn"
                      onClick={() => onUpdateFoodQuantity(foodId, mealType, quantity - 1)}
                    >
                      <i className="fas fa-minus"></i>
                    </button>
                    <span className="qty-value">{quantity}</span>
                    <button 
                      className="qty-btn"
                      onClick={() => onUpdateFoodQuantity(foodId, mealType, quantity + 1)}
                    >
                      <i className="fas fa-plus"></i>
                    </button>
                    <button 
                      className="remove-btn"
                      onClick={() => onRemoveFood(foodId, mealType)}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bucket-empty">
            <span>No items yet</span>
          </div>
        )}
      </div>

      {hasItems && (
        <div className="bucket-footer">
          <button className="clear-meal-btn" onClick={() => onClearMeal(mealType)}>
            <i className="fas fa-times"></i> Clear
          </button>
        </div>
      )}
    </div>
  );
};

export default MealBucket;
